"use client";

import { useSyncExternalStore } from "react";

import { ChannelActivityAvatar } from "@product/app/workspace/channels/[channelId]/channel-activity-avatar";
import { ChannelFeedRowView } from "@product/app/workspace/channels/[channelId]/channel-feed-row-view";
import { FeedControls, type FeedKindFilter } from "@product/components/app/feed/feed-controls";
import { FeedDayHeader, FeedSkeleton, groupFeedByDay } from "@product/components/app/feed/feed-day-groups";
import { Button } from "@product/components/ui/button";
import type { WorkspaceChannelFeedItem } from "@product/lib/mlx-workspace-client";

import { entryOccurredAt, type ShotChannelFeed, type ShotFeedEntry, type ShotFeedPost, type ShotFeedSession } from "./fixture-engineering";
import { ShotMentionMarkdown } from "./mention-markdown";

/**
 * The kinds a channel's feed can carry, as its type filter lists them
 * (channel-overview-model's CHANNEL_FEED_KINDS). Restated here: that module
 * imports the contracts package at runtime, which this site's build does not
 * have (it is built for the application only).
 */
const CHANNEL_FEED_KINDS = ["post", "discussion", "record", "session", "artifact", "file"] as const satisfies ReadonlyArray<Exclude<FeedKindFilter, "all">>;

const noChange = () => {};
const noReaction = async () => {};
const QUIET = { commentCount: 0, unreadCount: 0, participantIds: [], reactions: [] };
/** Shown from a frame 620px wide (the frame is the container, ShotAppFrame). */
const WIDE_FRAME_ONLY_CLASS = "hidden @min-[620px]:block";

/** Whether this render is in the browser: false on the server and while its markup hydrates. */
const never = () => () => {};
function useInBrowser(): boolean {
  return useSyncExternalStore(
    never,
    () => true,
    () => false,
  );
}

/** A post as the feed carries it, at the time it happened. */
function feedPost(post: ShotFeedPost, channelId: string, viewerId: string, at: number): WorkspaceChannelFeedItem {
  const mentioned = (type: NonNullable<ShotFeedPost["mentions"]>[number]["type"]) =>
    (post.mentions ?? []).filter((mention) => mention.type === type).map((mention) => mention.id);
  return {
    id: post.id,
    type: "post",
    occurredAt: at,
    post: {
      id: post.id,
      channelId,
      body: post.body,
      mentionedUserIds: mentioned("teammate"),
      mentionedChannelIds: mentioned("channel"),
      mentionedDataSetIds: mentioned("dataSet"),
      mentionedViewIds: mentioned("view"),
      mentionedEntityIds: [],
      mentionedEntities: [],
      authorId: post.authorId,
      authorType: "user",
      source: "web",
      createdAt: at,
      updatedAt: at,
      replyCount: post.replies,
    },
    engagement: {
      commentCount: post.replies,
      unreadCount: post.unreadReplies ?? 0,
      participantIds: [],
      reactions: (post.reactions ?? []).map(({ reaction, count, byViewer = false }) => ({
        reaction,
        count,
        reactedByViewer: byViewer,
        actors: byViewer ? [{ actorId: viewerId, actorType: "user" as const }] : [],
      })),
    },
  };
}

/** A session someone ran in the channel, finished, at the time it ran. */
function feedSession(session: ShotFeedSession, channelId: string, at: number): WorkspaceChannelFeedItem {
  return {
    id: session.id,
    type: "session",
    occurredAt: at,
    activity: {
      session: {
        id: session.id,
        userId: session.userId,
        title: session.title,
        status: "completed",
        visibility: "private",
        runtimeBackend: "electric",
        requestedPluginSlugs: [],
        resolvedPluginSlugs: [],
        requestedSkillSlugs: [],
        resolvedSkillSlugs: [],
        resolvedPluginSkillOverlays: [],
        requestedCapabilities: [],
        capabilityMode: "explicit",
        createdAt: at,
        updatedAt: at,
      },
      channelId,
      reasons: ["context", "read"],
      viewIds: [...session.viewIds],
      firstSeenAt: at,
      lastSeenAt: at,
      lastRunId: `run_${session.id}`,
    },
    relatedArtifacts: { items: [], totalCount: 0 },
    engagement: QUIET,
  };
}

function feedItem(entry: ShotFeedEntry, feed: ShotChannelFeed, at: number): WorkspaceChannelFeedItem {
  return entry.kind === "post" ? feedPost(entry, feed.channel.id, feed.currentUser.id, at) : feedSession(entry, feed.channel.id, at);
}

/** The viewer's initials, as the composer's avatar prints them (channel-feed-composer's `initials`). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/u).filter(Boolean);
  return (parts.length > 1 ? `${parts[0]?.[0] ?? ""}${parts.at(-1)?.[0] ?? ""}` : name.slice(0, 2)).toUpperCase();
}

/**
 * The feed's entries for a page seen at `now`, a day at a time in the
 * Feed's compact rows: the product's own rows, a post's text rendered as
 * the feed renders it, with its mentions (ShotMentionMarkdown). A day
 * whose entries are all `wideOnly` stays out of a frame too narrow to hold
 * it.
 */
export function ShotChannelFeedRows({ feed, now }: { feed: ShotChannelFeed; now: number }) {
  const membersByUserId = new Map(feed.directory.map((member) => [member.userId, member]));
  const viewsById = new Map(feed.views.map((view) => [view.id, view]));
  const entriesById = new Map(feed.entries.map((entry) => [entry.id, entry]));
  const items = feed.entries.map((entry) => feedItem(entry, feed, entryOccurredAt(entry, now)));
  return groupFeedByDay(items, (item) => item.occurredAt, now).map((day) => (
    <section
      key={day.id}
      aria-labelledby={`channel-feed-${day.id}`}
      className={day.items.every((item) => entriesById.get(item.id)?.wideOnly) ? WIDE_FRAME_ONLY_CLASS : undefined}
    >
      <FeedDayHeader id={`channel-feed-${day.id}`} label={day.label} />
      <ol>
        {day.items.map((item, index) => {
          const entry = entriesById.get(item.id);
          return (
            <ChannelFeedRowView
              key={item.id}
              item={item}
              channelId={feed.channel.id}
              currentUser={feed.currentUser}
              membersByUserId={membersByUserId}
              viewsById={viewsById}
              showConnector={index < day.items.length - 1}
              density="compact"
              reactions={{ engagement: item.engagement, pending: false, toggle: noReaction }}
              body={entry?.kind === "post" ? <ShotMentionMarkdown body={entry.body} mentions={entry.mentions} /> : undefined}
            />
          );
        })}
      </ol>
    </section>
  ));
}

/**
 * A channel's Feed tab at rest, as ChannelFeed draws it: the composer
 * closed, the feed's controls on everything, then the entries. The
 * composer is its closed markup, since the open one edits.
 *
 * The rows print each entry's time in the viewer's own clock, and a day's
 * label against the viewer's own today, which the server cannot know. So
 * they are drawn in the browser, and until then the feed shows as it does
 * while it loads.
 */
export function ShotChannelFeedView({ feed }: { feed: ShotChannelFeed }) {
  const inBrowser = useInBrowser();
  const viewerName = feed.currentUser.name ?? "You";
  const unread = feed.entries.some((entry) => entry.kind === "post" && (entry.unreadReplies ?? 0) > 0);
  return (
    <section className="w-full" aria-label="Channel feed">
      {/* ChannelFeedComposer, closed: one row of the mention input, empty. */}
      <div className="mlx-prompt-composer chat-composer-shadow mb-6 flex items-start gap-3 rounded-xl border border-transparent bg-control px-3 py-3">
        <ChannelActivityAvatar imageUrl={null} initials={initialsOf(viewerName)} label={viewerName} />
        <div className="min-w-0 flex-1">
          <p className="block min-h-8 w-full px-0 py-1 text-sm leading-5 text-placeholder">What do you want to share?</p>
        </div>
      </div>

      <FeedControls
        lens="all"
        onLensChange={noChange}
        actor="all"
        onActorChange={noChange}
        kind="all"
        onKindChange={noChange}
        kinds={CHANNEL_FEED_KINDS}
        trailing={
          <Button type="button" variant="outline" size="sm" disabled={!unread}>
            Mark all read
          </Button>
        }
      />

      {inBrowser ? <ShotChannelFeedRows feed={feed} now={Date.now()} /> : <FeedSkeleton />}
    </section>
  );
}
